<template>
  <div class="page road-exclusion-view">
    <div class="title_line flex items-center justify-between">
      <div class="title flex items-center gap-10">
        <router-link class="crumb" :to="{ name: 'AccountRoadExclusions' }">Road Exclusions</router-link>
        <span class="crumb-sep">/</span>
        <span class="name">{{ item.name || '' }}</span>
      </div>
      <div class="actions flex items-center gap-10" v-if="item.id">
        <button class="icon-btn border-green" type="button" @click="goEdit">
          <img src="@/assets/images/edit.svg" alt="edit" />
        </button>
        <button
          class="icon-btn"
          :class="item.is_active ? 'border-blue' : 'border-green'"
          type="button"
          @click="askToggle"
        >
          <img v-if="item.is_active" src="@/assets/images/ban.svg" alt="deactivate" />
          <img v-else src="@/assets/images/circle-check.svg" alt="activate" />
        </button>
        <button class="icon-btn border-red" type="button" @click="askDelete">
          <img src="@/assets/images/delete.svg" alt="delete" />
        </button>
      </div>
    </div>

    <div class="content-grid" v-if="item.id">
      <div class="cards">
        <div class="card">
          <div class="card-title">Info</div>
          <div class="row"><span class="label">Name</span><span class="value">{{ item.name }}</span></div>
          <div class="row"><span class="label">Segment ID</span><span class="value">{{ item.segment_id }}</span></div>
          <div class="row"><span class="label">Segment length</span><span class="value">{{ lengthLabel }}</span></div>
          <div class="row"><span class="label">From</span><span class="value">{{ formatDate(item.valid_from) }}</span></div>
          <div class="row"><span class="label">To</span><span class="value">{{ formatDate(item.valid_to) }}</span></div>
          <div class="row"><span class="label">Status</span><span class="value" :class="item.is_active ? 'status-active' : 'status-inactive'">{{ item.is_active ? 'Active' : 'Inactive' }}</span></div>
          <div class="row"><span class="label">Added</span><span class="value">{{ formatDate(item.created_at) }}</span></div>
        </div>
        <div class="card">
          <div class="card-title">Restriction Parameters</div>
          <div class="row"><span class="label">Weight</span><span class="value">{{ dim(item.weight_min, 't') }}</span></div>
          <div class="row"><span class="label">Width</span><span class="value">{{ dim(item.width_min, 'm') }}</span></div>
          <div class="row"><span class="label">Height</span><span class="value">{{ dim(item.height_min, 'm') }}</span></div>
          <div class="row"><span class="label">Length</span><span class="value">{{ dim(item.length_min, 'm') }}</span></div>
        </div>
      </div>
      <div class="map-wrap">
        <div ref="mapContainer" class="map-container"></div>
      </div>
    </div>

    <ConfirmActionModal
      :show="confirm.show"
      :title="confirm.title"
      @cancel="confirm.show = false"
      @confirm="runConfirm"
    />
  </div>
</template>

<script>
import ConfirmActionModal from '@/components/ConfirmActionModal.vue';
import { infoMessage } from '@/services/messages';
import pinIconUrl from '@/assets/images/pin.svg';

export default {
  name: 'RoadExclusionView',
  components: { ConfirmActionModal },
  props: {
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      item: {},
      map: null,
      marker: null,
      segmentLine: null,
      confirm: { show: false, title: '', action: null },
    };
  },
  computed: {
    viewId() {
      return this.id || this.$route.params.id;
    },
    lengthLabel() {
      if (this.item.segment_length == null) return '—';
      return Number(this.item.segment_length).toFixed(1) + ' m';
    },
  },
  async mounted() {
    try {
      await this.load();
    } catch (e) {
      console.error(e);
      infoMessage('Failed to load map', 'danger');
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    if (this.map) {
      this.map.dispose();
      this.map = null;
    }
  },
  methods: {
    apiBase() {
      return this.$http.apiUrl() + '/road-exclusions';
    },
    formatDate(val) {
      if (!val) return '—';
      return String(val).slice(0, 16).replace('T', ' ');
    },
    dim(val, unit) {
      if (val == null || val === '') return '—';
      return Number(val) + ' ' + unit;
    },
    async load() {
      try {
        const response = await this.$http.getAuth(this.apiBase() + '/' + this.viewId);
        this.item = (response.data && response.data.item) || {};
        if (this.item.segment_id) {
          await this.ensureFreshSegmentGeometry();
        }
        await this.$nextTick();
        await this.initMap();
      } catch (e) {
        infoMessage('Failed to load road exclusion', 'danger');
      }
    },
    async ensureFreshSegmentGeometry() {
      try {
        const response = await this.$http.getAuth(
          this.apiBase() +
            '/segment-geometry/' +
            encodeURIComponent(this.item.segment_id),
        );
        const data = response.data || {};
        if (data.segment_polyline || data.segment_geometry) {
          this.item = {
            ...this.item,
            segment_polyline: data.segment_polyline || this.item.segment_polyline,
            segment_geometry: data.segment_geometry || this.item.segment_geometry,
            segment_length:
              data.length_m != null ? data.length_m : this.item.segment_length,
          };
        }
      } catch (e) {
        console.error(e);
      }
    },
    ensureHereCss() {
      const href = 'https://js.api.here.com/v3/3.2/mapsjs-ui.css';
      if (document.querySelector(`link[href="${href}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = href;
      document.head.appendChild(link);
    },
    loadScript(src) {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
          if (window.H && window.H.Map) return resolve();
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', reject);
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async loadHereMapsAPI() {
      if (window.H && window.H.Map) {
        return;
      }
      this.ensureHereCss();
      const scripts = [
        'https://js.api.here.com/v3/3.2/mapsjs-core.js',
        'https://js.api.here.com/v3/3.2/mapsjs-mapevents.js',
        'https://js.api.here.com/v3/3.2/mapsjs-service.js',
        'https://js.api.here.com/v3/3.2/mapsjs-ui.js',
      ];
      for (const src of scripts) {
        await this.loadScript(src);
      }
      if (!window.H || !window.H.Map) {
        throw new Error('HERE Maps API failed to load');
      }
    },
    async initMap() {
      if (!this.$refs.mapContainer) {
        throw new Error('Map container not found');
      }
      if (!process.env.VUE_APP_HERE_API_KEY) {
        throw new Error('HERE API key is not configured (VUE_APP_HERE_API_KEY)');
      }
      if (this.map) {
        window.removeEventListener('resize', this.onWindowResize);
        this.map.dispose();
        this.map = null;
        this.marker = null;
        this.segmentLine = null;
      }
      await this.loadHereMapsAPI();
      const platform = new H.service.Platform({
        apikey: process.env.VUE_APP_HERE_API_KEY,
      });
      const engineType = H.Map.EngineType.HARP;
      const defaultLayers = platform.createDefaultLayers({ engineType });
      const baseLayer =
        (defaultLayers.vector.normal && defaultLayers.vector.normal.logistics) ||
        defaultLayers.vector.normal.map;
      const center =
        this.item.click_lat != null
          ? { lat: this.item.click_lat, lng: this.item.click_lng }
          : { lat: 51.5074, lng: -0.1278 };
      this.map = new H.Map(this.$refs.mapContainer, baseLayer, {
        zoom: 15,
        center,
        engineType,
      });
      new H.mapevents.Behavior(new H.mapevents.MapEvents(this.map));
      window.addEventListener('resize', this.onWindowResize);

      await this.$nextTick();
      this.scheduleMapResize();

      if (this.item.click_lat != null) {
        const icon = new H.map.Icon(pinIconUrl, {
          size: { w: 30, h: 36 },
          anchor: { x: 15, y: 36 },
        });
        this.marker = new H.map.Marker(
          { lat: this.item.click_lat, lng: this.item.click_lng },
          { icon },
        );
        this.map.addObject(this.marker);
      }

      const linestring = this.buildSegmentLineString();
      if (linestring) {
        this.segmentLine = new H.map.Polyline(linestring, {
          style: { strokeColor: '#FF0000', lineWidth: 8 },
        });
        this.map.addObject(this.segmentLine);
        const bbox = linestring.getBoundingBox && linestring.getBoundingBox();
        if (bbox) {
          try {
            this.map.getViewModel().setLookAtData({
              bounds: bbox,
              padding: { top: 80, right: 80, bottom: 80, left: 80 },
            });
          } catch (e) {
            console.error(e);
          }
          await this.$nextTick();
          const zoom = this.map.getZoom();
          if (zoom > 18) this.map.setZoom(18);
          if (zoom < 14) this.map.setZoom(16);
        }
      } else if (this.item.click_lat != null) {
        this.map.setCenter({
          lat: this.item.click_lat,
          lng: this.item.click_lng,
        });
        this.map.setZoom(16);
      }

      await this.$nextTick();
      if (this.map) this.map.getViewPort().resize();
    },
    buildSegmentLineString() {
      if (this.item.segment_polyline) {
        try {
          return H.geo.LineString.fromFlexiblePolyline(this.item.segment_polyline);
        } catch (e) {
          console.error(e);
        }
      }
      const coords =
        this.item.segment_geometry && this.item.segment_geometry.coordinates;
      if (Array.isArray(coords) && coords.length >= 2) {
        const linestring = new H.geo.LineString();
        coords.forEach((pair) => {
          if (!Array.isArray(pair) || pair.length < 2) return;
          const lng = Number(pair[0]);
          const lat = Number(pair[1]);
          if (Number.isNaN(lat) || Number.isNaN(lng)) return;
          linestring.pushPoint({ lat, lng });
        });
        if (linestring.getPointCount() >= 2) return linestring;
      }
      return null;
    },
    scheduleMapResize() {
      requestAnimationFrame(() => {
        if (this.map) this.map.getViewPort().resize();
        setTimeout(() => {
          if (this.map) this.map.getViewPort().resize();
        }, 100);
      });
    },
    onWindowResize() {
      if (this.map) this.map.getViewPort().resize();
    },
    goEdit() {
      this.$router.push({
        name: 'EditAccountRoadExclusion',
        params: { id: String(this.viewId) },
      });
    },
    askToggle() {
      const next = !this.item.is_active;
      this.confirm = {
        show: true,
        title: this.item.is_active
          ? 'Set this road exclusion to Inactive?'
          : 'Set this road exclusion to Active?',
        action: async () => {
          const axios = require('axios');
          await axios.patch(
            this.apiBase() + '/' + this.viewId + '/status',
            { is_active: next },
            { headers: this.$http.getAuthHeader() },
          );
          await this.load();
        },
      };
    },
    askDelete() {
      this.confirm = {
        show: true,
        title: 'Delete this road exclusion?',
        action: async () => {
          const axios = require('axios');
          await axios.delete(this.apiBase() + '/' + this.viewId, {
            headers: this.$http.getAuthHeader(),
          });
          this.$router.push({ name: 'AccountRoadExclusions' });
        },
      };
    },
    async runConfirm() {
      try {
        if (this.confirm.action) await this.confirm.action();
      } catch (e) {
        infoMessage('Action failed', 'danger');
      } finally {
        this.confirm.show = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.page.road-exclusion-view {
  width: 100%;
  min-height: calc(100vh - 280px);
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.crumb {
  font-family: Catamaran, sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 18px;
  color: #1FBC2F;
  text-decoration: none;
}
.crumb-sep {
  font-family: Catamaran, sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 18px;
  color: #000000;
}
.name {
  font-family: Catamaran, sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 18px;
  color: #000000;
}
.icon-btn {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  padding: 10px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  &.border-green { border: 2px solid #1FBC2F; }
  &.border-blue { border: 2px solid #2468CA; }
  &.border-red { border: 2px solid #F2374D; }
  img { width: 20px; height: 20px; }
}
.content-grid {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  text-align: left;
  min-height: 0;
  width: 100%;
}
.cards {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-shrink: 0;
}
.card {
  background: #FFFFFF;
  border: 1px solid #E2E4E6;
  border-radius: 12px;
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
  .card-title {
    font-family: Catamaran, sans-serif;
    font-weight: 700;
    font-size: 18px;
    line-height: 18px;
    color: #22292F;
    margin-bottom: 16px;
  }
  .row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    font-family: Catamaran, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 14px;
    color: #303741;
  }
}
.map-wrap {
  flex: 1;
  width: 100%;
  min-width: 0;
  align-self: stretch;
  min-height: 600px;
}
.map-container {
  width: 100%;
  height: 100%;
  min-height: 600px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}
.status-active { color: #0F9E1D; font-weight: 600; }
.status-inactive { color: #969A9F; font-weight: 600; }
.gap-10 { gap: 10px; }
</style>
